import { useState, useEffect, useRef, useCallback } from 'react';
import { apiErrorText } from '../lib/apiError';
import axios from 'axios';
import { Loader2, ShieldCheck, Fingerprint, Smartphone, Mail, Check } from 'lucide-react';
import StandardModal from './StandardModal';
import { SecondaryButton, PrimaryButton } from './Buttons';
import { Input } from './ui/input';
import { API_URL } from '../config/api';
import { waGet } from '../lib/webauthn';

/**
 * Step-up ("sudo mode") confirmation for dangerous Platform Admin actions.
 * BookingFlows super admins must present TWO different factors — any combination of:
 *   • Security key / passkey (FIDO2) tap
 *   • Authenticator (TOTP) code
 *   • Emailed one-time code (fallback)
 * Each verified factor is added to a server-side sudo token; once the required
 * count is reached the token authorizes the action for ~10 minutes (cached here,
 * so back-to-back actions skip re-verification).
 */
let stepUpCache = null; // { token, until }

const AUTH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` }, timeout: 30000 });

const FACTOR_META = {
  fido: { label: 'Security key or passkey', hint: 'Touch your key or use Face ID / Touch ID', icon: Fingerprint },
  totp: { label: 'Authenticator app', hint: 'Enter the 6-digit code from your app', icon: Smartphone },
  email: { label: 'Emailed code', hint: 'Enter the code we sent you', icon: Mail },
};

const PinModal = ({ prompt, onClose }) => {
  const [phase, setPhase] = useState('loading'); // loading | confirm | pick | totp | email | fido
  const [plan, setPlan] = useState(null);        // { available, required, masked_destinations }
  const [token, setToken] = useState(null);
  const [done, setDone] = useState([]);          // collected factor names
  const [active, setActive] = useState(null);    // factor currently being entered
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const runAction = useCallback(async (tok) => {
    setBusy(true);
    setError('');
    try {
      await prompt.run('', tok);
      stepUpCache = { token: tok, until: Date.now() + 9 * 60 * 1000 };
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === 'STEPUP_REQUIRED') {
        stepUpCache = null;
        setError('Your verification expired — please confirm again.');
        return start();
      }
      setError(err?.friendlyMessage || apiErrorText(err, 'Network error — please try again'));
      setBusy(false);
    }
  }, [prompt, onClose]);

  const applyGrant = useCallback(async (g) => {
    setToken(g.stepup_token);
    setDone(g.factors || []);
    if (g.satisfied) return runAction(g.stepup_token);
    setActive(null);
    setCode('');
    setPhase('pick');
    setBusy(false);
  }, [runAction]);

  const start = useCallback(async () => {
    setPhase('loading');
    setError('');
    setCode('');
    setToken(null);
    setDone([]);
    setActive(null);
    try {
      const qs = prompt?.level === 'single' ? '?level=single' : '';
      const res = await axios.post(`${API_URL}/v2/super-admin/security/stepup/start${qs}`, {}, AUTH());
      setPlan(res.data);
      setPhase('pick');
    } catch (err) {
      setError(apiErrorText(err, 'Could not start verification — please try again.'));
      setPhase('pick');
    }
  }, [prompt]);

  useEffect(() => {
    if (!prompt) return;
    setError('');
    setBusy(false);
    if (stepUpCache && stepUpCache.until > Date.now()) {
      setToken(stepUpCache.token);
      setPhase('confirm');
    } else {
      start();
    }
  }, [prompt]);

  const runFido = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const opts = await axios.post(`${API_URL}/v2/super-admin/security/webauthn/stepup-options`, { origin: window.location.origin }, AUTH());
      const credential = await waGet(opts.data);
      const v = await axios.post(`${API_URL}/v2/super-admin/security/webauthn/stepup-verify`,
        { credential, origin: window.location.origin, stepup_token: token, need: plan?.required }, AUTH());
      await applyGrant(v.data);
    } catch (err) {
      setBusy(false);
      if (err?.name === 'NotAllowedError') setError('Key touch cancelled or timed out — try again.');
      else setError(apiErrorText(err, 'Security key verification failed — try again.'));
      setPhase('pick');
      setActive(null);
    }
  }, [token, applyGrant, plan]);

  const submitCode = async () => {
    if (!/^\d{6}$/.test(code)) { setError('Enter the 6-digit code'); return; }
    setBusy(true);
    setError('');
    const url = active === 'totp'
      ? `${API_URL}/v2/super-admin/security/totp/stepup-verify`
      : `${API_URL}/v2/super-admin/security/email/stepup-verify`;
    try {
      const v = await axios.post(url, { code, stepup_token: token, need: plan?.required }, AUTH());
      await applyGrant(v.data);
    } catch (err) {
      setBusy(false);
      setError(apiErrorText(err, 'Invalid code — please try again.'));
      setCode('');
    }
  };

  const pickFactor = (f) => {
    setActive(f);
    setError('');
    setCode('');
    if (f === 'fido') { setPhase('fido'); return runFido(); }
    setPhase(f === 'totp' ? 'totp' : 'email');
    setTimeout(() => inputRef.current?.focus(), 150);
  };

  const resendEmail = async () => {
    setBusy(true);
    try {
      const qs = prompt?.level === 'single' ? '?level=single' : '';
      const res = await axios.post(`${API_URL}/v2/super-admin/security/stepup/start${qs}`, {}, AUTH());
      setPlan((p) => ({ ...p, masked_destinations: res.data.masked_destinations }));
      setError('A fresh code was sent.');
    } catch {
      setError('Could not resend — try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  const available = plan?.available || [];
  const required = plan?.required || 2;
  const remaining = Math.max(0, required - done.length);
  const title = phase === 'totp' ? 'Enter your authenticator code'
    : phase === 'email' ? 'Enter your emailed code'
    : phase === 'confirm' ? 'Confirm this action'
    : 'Verify it\'s you';

  const footer = phase === 'confirm' ? (
    <>
      <SecondaryButton onClick={onClose} className="flex-1" disabled={busy}>Cancel</SecondaryButton>
      <PrimaryButton onClick={() => runAction(token)} className="flex-1" disabled={busy} data-testid="pin-confirm-button">
        {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Confirming...</>) : (<><ShieldCheck className="w-4 h-4 mr-2" />Confirm</>)}
      </PrimaryButton>
    </>
  ) : (phase === 'totp' || phase === 'email') ? (
    <>
      <SecondaryButton onClick={() => { setPhase('pick'); setActive(null); setError(''); }} className="flex-1" disabled={busy}>Back</SecondaryButton>
      <PrimaryButton onClick={submitCode} className="flex-1" disabled={busy} data-testid="pin-confirm-button">
        {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifying...</>) : (<><ShieldCheck className="w-4 h-4 mr-2" />Verify</>)}
      </PrimaryButton>
    </>
  ) : (
    <SecondaryButton onClick={onClose} className="flex-1" disabled={busy}>Cancel</SecondaryButton>
  );

  return (
    <StandardModal isOpen={!!prompt} onClose={busy ? () => {} : onClose} title={title} size="sm" footer={footer}>
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-4 h-4 text-blue-600" />
          </div>
          <p className="text-sm text-gray-600 pt-1.5" data-testid="pin-modal-action-label">{prompt?.label}</p>
        </div>

        {phase === 'loading' && (
          <div className="flex items-center justify-center gap-2 py-4 text-sm text-gray-500" data-testid="stepup-loading">
            <Loader2 className="w-4 h-4 animate-spin" />Preparing verification…
          </div>
        )}

        {phase === 'confirm' && (
          <p className="text-sm text-gray-600 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2.5" data-testid="stepup-cached-note">
            <ShieldCheck className="w-3.5 h-3.5 inline mr-1.5 text-emerald-600" />
            You verified a moment ago — click Confirm to proceed.
          </p>
        )}

        {phase === 'pick' && (
          <div className="space-y-3" data-testid="stepup-factor-picker">
            {required >= 2 ? (
              <p className="text-xs font-medium text-gray-500" data-testid="stepup-progress">
                Verify with <span className="text-gray-800">{required} different methods</span> — {done.length}/{required} done
              </p>
            ) : (
              <p className="text-xs font-medium text-gray-500" data-testid="stepup-progress">
                Confirm with <span className="text-gray-800">one of your methods</span>
              </p>
            )}
            {available.map((f) => {
              const meta = FACTOR_META[f];
              const Icon = meta.icon;
              const isDone = done.includes(f);
              return (
                <button
                  key={f}
                  type="button"
                  disabled={isDone || busy}
                  onClick={() => pickFactor(f)}
                  data-testid={`stepup-factor-${f}`}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl border text-left transition-colors ${isDone ? 'bg-emerald-50 border-emerald-200 cursor-default' : 'bg-white border-gray-200 hover:border-blue-400 hover:bg-blue-50/40'}`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isDone ? 'bg-emerald-100' : 'bg-gray-100'}`}>
                    {isDone ? <Check className="w-4 h-4 text-emerald-600" /> : <Icon className="w-4 h-4 text-gray-600" />}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-800">{meta.label}</div>
                    <div className="text-xs text-gray-400">{isDone ? 'Verified' : meta.hint}</div>
                  </div>
                </button>
              );
            })}
            {remaining > 0 && required >= 2 && (
              <p className="text-xs text-gray-400">Pick {remaining} {remaining === 1 ? 'method' : 'more methods'} — they must be different.</p>
            )}
          </div>
        )}

        {phase === 'fido' && (
          <div className="flex flex-col items-center gap-3 py-2" data-testid="stepup-fido-panel">
            <div className="w-14 h-14 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center">
              {busy ? <Loader2 className="w-6 h-6 text-blue-600 animate-spin" /> : <Fingerprint className="w-6 h-6 text-blue-600" />}
            </div>
            <p className="text-sm text-gray-600 text-center">
              {busy ? 'Waiting for your security key or passkey — confirm on your device.' : 'Ready to verify with your security key or passkey.'}
            </p>
          </div>
        )}

        {(phase === 'totp' || phase === 'email') && (
          <>
            <p className="text-xs text-gray-500">
              {phase === 'email'
                ? <>Code sent to <span className="font-medium text-gray-700" data-testid="stepup-masked-destinations">{plan?.masked_destinations || 'your 2FA channels'}</span></>
                : 'Open your authenticator app and enter the current 6-digit code.'}
            </p>
            <Input
              ref={inputRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              data-1p-ignore
              data-lpignore="true"
              spellCheck={false}
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => e.key === 'Enter' && submitCode()}
              placeholder="000000"
              className="h-12 text-center text-2xl tracking-[0.4em] font-mono"
              data-testid={phase === 'totp' ? 'stepup-totp-input' : 'stepup-otp-input'}
            />
            {phase === 'email' && (
              <button type="button" onClick={resendEmail} disabled={busy} className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-40" data-testid="stepup-resend-button">
                Resend code
              </button>
            )}
          </>
        )}

        {error && <p className="text-sm text-red-600" data-testid="pin-error">{error}</p>}
        <p className="text-xs text-gray-400">This action is recorded in the audit trail.</p>
      </div>
    </StandardModal>
  );
};

export default PinModal;
